import type { Case, CaseListItem } from "../api/types.ts"
import type { Locale } from "../i18n/index.tsx"
import type { Translator } from "../i18n/translate.ts"
import { formatTracking } from "../lib/tracking.ts"
import type { Column, ColumnPriority } from "../ui/index.ts"
import { DocBadge, StatusBadge } from "./badges.tsx"
import { formatJpy, formatWeight, nullable } from "./format.ts"

/**
 * 목록·스캔·선적 화면이 같이 쓰는 반품 칸 정의.
 *
 * 네 화면이 각자 적어 두면 한 곳만 고쳐져 "목록에서는 운송장에 하이픈이 있고 선적에서는 없는"
 * 차이가 생긴다. **이름표와 값 만드는 법**은 여기 한 곳에서 정한다.
 *
 * 무엇을 먼저 접을지(`priority`)와 카드 제목으로 올릴 칸(`primary`)은 화면마다 다르다
 * — 스캔 화면의 주인공은 운송장번호이고 목록의 주인공은 주문번호다. 그 둘만 화면이 넘긴다.
 */
export type ColumnLayout = { priority?: ColumnPriority; primary?: boolean }

const layoutable =
  <T,>(base: Column<T>) =>
  (layout: ColumnLayout = {}): Column<T> => ({ ...base, ...layout })

export function caseColumns(t: Translator, locale: Locale) {
  return {
    seq: layoutable<Case>({
      key: "seq",
      header: t("case.seq"),
      sortKey: "seq",
      align: "end",
      render: (row) => row.seq,
    }),

    orderNo: layoutable<Case>({
      key: "orderNo",
      header: t("case.orderNo"),
      sortKey: "orderNo",
      render: (row) => row.orderNo,
    }),

    customerName: layoutable<Case>({
      key: "customerName",
      header: t("case.customerName"),
      sortKey: "customerName",
      render: (row) => row.customerName,
    }),

    status: layoutable<Case>({
      key: "status",
      header: t("case.status"),
      sortKey: "status",
      render: (row) => <StatusBadge status={row.status} />,
    }),

    docStatus: layoutable<Case>({
      key: "docStatus",
      header: t("case.docStatus"),
      render: (row) => <DocBadge docStatus={row.docStatus} />,
    }),

    receivedOn: layoutable<Case>({
      key: "receivedOn",
      header: t("case.receivedOn"),
      sortKey: "receivedOn",
      render: (row) => row.receivedOn,
    }),

    /** 저장은 숫자만, 보여 줄 때만 하이픈을 붙인다(`lib/tracking.ts`). */
    trackingNo: layoutable<Case>({
      key: "trackingNo",
      header: t("case.trackingNo"),
      render: (row) => nullable(formatTracking(row.trackingNo)),
    }),

    weight: layoutable<Case>({
      key: "weight",
      sortKey: "weightG",
      header: t("case.weight"),
      align: "end",
      render: (row) => formatWeight(row.weightG),
    }),

    disposition: layoutable<Case>({
      key: "disposition",
      header: t("case.disposition"),
      render: (row) => (row.disposition ? t(`disposition.${row.disposition}`) : "—"),
    }),

    /** 화주 이름은 목록 응답에만 실려 온다(`CaseListItem`). */
    org: layoutable<CaseListItem>({
      key: "org",
      header: t("case.org"),
      render: (row) => (locale === "ja" ? row.orgNameJa : row.orgNameKo),
    }),

    // ── 기본으로는 접혀 있는 칸 ──────────────────────────────────────────
    // 늘 보여 주면 표가 가로로 넘치지만, 어떤 사람에게는 이 칸이 주인공이다.
    // "컬럼 설정"(components/ColumnPicker.tsx)에서 골라 켠다.

    returnReason: layoutable<Case>({
      key: "returnReason",
      header: t("case.returnReason"),
      render: (row) => (row.returnReason ? t(`reason.${row.returnReason}`) : "—"),
    }),

    customerPhone: layoutable<Case>({
      key: "customerPhone",
      header: t("case.customerPhone"),
      render: (row) => nullable(row.customerPhone),
    }),

    arrivedOn: layoutable<Case>({
      key: "arrivedOn",
      header: t("case.arrivedOn"),
      sortKey: "arrivedOn",
      render: (row) => nullable(row.arrivedOn),
    }),

    inspected: layoutable<Case>({
      key: "inspected",
      header: t("caseDetail.inspectedAt"),
      render: (row) => (row.inspectedAt ? row.inspectedAt.slice(0, 10) : "—"),
    }),

    freight: layoutable<Case>({
      key: "freight",
      header: t("case.freight"),
      render: (row) => (row.freightType ? t(`freight.${row.freightType}`) : "—"),
    }),

    codFee: layoutable<Case>({
      key: "codFee",
      header: t("case.codFee"),
      align: "end",
      render: (row) => formatJpy(row.codFeeJpy),
    }),

    closedOn: layoutable<Case>({
      key: "closedOn",
      header: t("case.closedOn"),
      sortKey: "closedOn",
      render: (row) => nullable(row.closedOn),
    }),

    itemCount: layoutable<CaseListItem>({
      key: "itemCount",
      header: t("case.itemCount"),
      align: "end",
      render: (row) => row.itemCount,
    }),
  }
}

/** 옆판·카드 제목. 어느 화면에서 열든 같은 문구여야 한다. */
export function caseTitle(t: Translator, row: Pick<Case, "seq" | "orderNo">): string {
  return `${t("caseDetail.title")} #${row.seq} · ${row.orderNo}`
}
